<script setup>
import { reactive, computed } from 'vue';
import { useMediaQuery } from '@vueuse/core';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

import Editor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import { useContactDetailI18n } from '../useContactDetailI18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactNoteRow from './components/ContactNoteRow.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';

const { t } = useContactDetailI18n();
const store = useStore();
const { formatMessage } = useMessageFormatter();
const hasText = content =>
  new DOMParser()
    .parseFromString(formatMessage(content || ''), 'text/html')
    .body.textContent.trim().length > 0;
const route = useRoute();

const state = reactive({
  message: '',
});
const isMobileViewport = useMediaQuery('(max-width: 767px)');
const desktopMenuOptions = ['strong', 'em', 'strike', 'bulletList'];
const mobileMenuOptions = [
  'strong',
  'em',
  'code',
  'link',
  'strike',
  'bulletList',
  'orderedList',
  'undo',
  'redo',
];
const noteMenuOptions = computed(() =>
  isMobileViewport.value ? mobileMenuOptions : desktopMenuOptions
);

const { isAdmin } = useAdmin();
const currentUser = useMapGetter('getCurrentUser');
const notesByContact = useMapGetter('contactNotes/getAllNotesByContactId');
const uiFlags = useMapGetter('contactNotes/getUIFlags');
const isFetchingNotes = computed(() => uiFlags.value.isFetching);
const isCreatingNote = computed(() => uiFlags.value.isCreating);
const notes = computed(() => notesByContact.value(route.params.contactId));

const getWrittenBy = note => {
  const isCurrentUser = note?.user?.id === currentUser.value.id;
  return isCurrentUser
    ? t('CONTACTS_LAYOUT.SIDEBAR.NOTES.YOU')
    : note?.user?.name || 'Bot';
};

const onAdd = async content => {
  if (!hasText(content) || isCreatingNote.value) return;
  const { contactId } = route.params;
  try {
    await store.dispatch('contactNotes/create', { content, contactId });
    if (state.message === content) state.message = '';
  } catch {
    useAlert(t('CONTACT_DETAIL.NOTE_ERROR'));
  }
};

const onDelete = async noteId => {
  if (!noteId || uiFlags.value.isDeleting) return;
  const { contactId } = route.params;
  try {
    await store.dispatch('contactNotes/delete', { noteId, contactId });
    useAlert(t('CONTACT_DETAIL.NOTE_DELETED'));
  } catch {
    useAlert(t('CONTACT_DETAIL.NOTE_DELETE_ERROR'));
  }
};

const onUpdate = async ({ noteId, content }) => {
  await store.dispatch('contactNotes/update', {
    noteId,
    content,
    contactId: route.params.contactId,
  });
};

const onEditorKeydown = event => {
  if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
    event.preventDefault();
    event.stopPropagation();
    onAdd(state.message);
  }
};
</script>

<template>
  <div
    class="flex flex-col gap-4 max-md:gap-2 max-md:rounded-[18px] max-md:bg-white max-md:p-2 max-md:shadow-[0_12px_32px_rgba(0,0,0,0.05)]"
    data-testid="contact-notes"
  >
    <div
      class="overflow-hidden rounded-[20px] bg-white shadow-[0_12px_32px_rgba(0,0,0,0.05)] max-md:min-w-0 max-md:rounded-none max-md:shadow-none"
      @keydown.capture="onEditorKeydown"
    >
      <div class="max-md:overflow-hidden max-md:rounded-xl max-md:bg-[#f5f5f5]">
        <Editor
          :key="isMobileViewport ? 'notas-mobile' : 'notas-web'"
          :model-value="state.message"
          :placeholder="t('CONTACT_DETAIL.NOTES_PLACEHOLDER')"
          :focus-on-mount="false"
          :disabled="isCreatingNote"
          :enable-canned-responses="false"
          :enabled-menu-options="noteMenuOptions"
          class="contact-notes-mobile-toolbar [&_.ProseMirror-menubar-wrapper]:!min-h-[144px] [&_.ProseMirror-menubar-wrapper]:!gap-0 [&_.ProseMirror-menubar]:!static [&_.ProseMirror-menubar]:!h-11 [&_.ProseMirror-menubar]:!box-border [&_.ProseMirror-menubar]:!m-0 [&_.ProseMirror-menubar]:!w-full [&_.ProseMirror-menubar]:!max-w-none [&_.ProseMirror-menubar]:!rounded-none [&_.ProseMirror-menubar]:!border-0 [&_.ProseMirror-menubar]:!border-b [&_.ProseMirror-menubar]:!border-n-weak [&_.ProseMirror-menubar]:!bg-[#fafafa] [&_.ProseMirror-menubar]:!px-4 [&_.ProseMirror-menubar]:!py-3 [&_.ProseMirror]:!px-4 [&_.ProseMirror]:!py-4 max-md:[&_.ProseMirror-menubar-wrapper]:!min-h-0 max-md:[&_.ProseMirror-menubar]:!left-0 max-md:[&_.ProseMirror-menubar]:!min-h-10 max-md:[&_.ProseMirror-menubar]:!h-auto max-md:[&_.ProseMirror-menubar]:!grid max-md:[&_.ProseMirror-menubar]:!grid-cols-[repeat(14,minmax(0,1fr))] max-md:[&_.ProseMirror-menubar]:!justify-items-center max-md:[&_.ProseMirror-menubar]:!gap-1 max-md:[&_.ProseMirror-menubar]:!overflow-visible max-md:[&_.ProseMirror-menubar]:!bg-transparent max-md:[&_.ProseMirror-menubar]:!px-2 max-md:[&_.ProseMirror-menubar]:!py-1 max-md:[&_.ProseMirror-menuitem]:!size-7 max-md:[&_.ProseMirror-menuitem]:!col-span-2 max-md:[&_.ProseMirror-menuitem:nth-last-child(2)]:!col-start-6 max-md:[&_.ProseMirror-menuitem:nth-last-child(2)]:!row-start-2 max-md:[&_.ProseMirror-menuitem:last-child]:!col-start-8 max-md:[&_.ProseMirror-menuitem:last-child]:!row-start-2 max-md:[&_.ProseMirror]:!min-h-[88px] max-md:[&_.ProseMirror]:!bg-transparent max-md:[&_.ProseMirror]:!px-3 max-md:[&_.ProseMirror]:!py-3"
          @input="state.message = $event"
        />
        <div
          class="flex flex-wrap items-center justify-between gap-3 px-4 pb-3 pt-2 max-md:px-3"
        >
          <span class="text-xs text-n-slate-10 max-md:hidden">{{
            t('CONTACT_DETAIL.NOTES_PRIVATE')
          }}</span>
          <Button
            size="sm"
            :label="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.SAVE')"
            class="ml-auto !h-9 !rounded-lg !bg-[#16A34A] disabled:!bg-[#BBE5C8] disabled:!opacity-100 !text-white max-md:!min-w-[100px] max-md:!justify-center max-md:!shadow-none max-md:!px-3.5 max-md:disabled:!text-[#3f7e64]"
            :is-loading="isCreatingNote"
            :disabled="!hasText(state.message) || isCreatingNote"
            @click="onAdd(state.message)"
          />
        </div>
      </div>
    </div>
    <div
      v-if="isFetchingNotes"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div
      v-else-if="notes.length > 0"
      class="rounded-[18px] bg-white px-5 py-1.5 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_32px_rgba(16,24,40,0.05)] max-md:px-2 max-md:shadow-none"
    >
      <ContactNoteRow
        v-for="note in notes"
        :key="note.id"
        :note="note"
        :written-by="getWrittenBy(note)"
        :can-edit="isAdmin || note.user?.id === currentUser.id"
        :on-update="onUpdate"
        @delete="onDelete"
      />
    </div>
    <p
      v-else
      class="px-6 py-6 text-center text-[13px] leading-6 text-[#8C959F] max-md:px-3 max-md:py-5"
    >
      {{ t('CONTACT_DETAIL.NOTES_EMPTY') }}
    </p>
  </div>
</template>
